import { useDragLayer } from "react-dnd";
import { useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { nanoid } from "nanoid";
import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { usePlaySound } from "common/utils";
import { AssetModel, AssetModelType } from "common/model";
import { actions } from "features/builder/slice";
import { useActionCommand } from "features/builder/hook";
import { ActionCommandType } from "features/builder/types";
import { createPreviewMesh } from "./loader";
import { createGround, createOuterNode, canPlaceOnGround } from "./util";

export const useDndScene = (scene: Scene | null) => {
  const play = usePlaySound();
  const dispatch = useDispatch();
  const { handleAction } = useActionCommand();
  const [creating, setCreating] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [previewedMesh, setPreviewedMesh] = useState<Mesh | null>(null);
  const [selectedModel, setSelectedModel] = useState<AssetModel | null>(null);

  const { item, clientOffset, isDragging } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    clientOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  useEffect(() => {
    if (isDragging) {
      const camera = scene.getCameraByName("camera") as ArcRotateCamera;
      camera?.detachControl();
      // カメラを detach すると Babylon が canvas の touch-action を戻すため、
      // ドラッグ中にページ側のスクロール/ピンチが割り込まないよう明示的に止める
      const canvas = scene.getEngine().getRenderingCanvas();
      if (canvas) canvas.style.touchAction = "none";
      setSelectedModel(item);
      scene.getMaterialByName("ground_layer").alpha = 0.3;
      if (item.type === AssetModelType.RIVER || item.type === AssetModelType.GROUND) {
        scene.getMaterialByName("ground").alpha = 0.3;
      }
    } else {
      if (scene && selectedModel) {
        setSelectedModel(null);
        setOffset({ x: 0, y: 0 });
        scene.getMaterialByName("ground").alpha = 0;
        scene.getMaterialByName("ground_layer").alpha = 0;
        if (previewedMesh) {
          // 新しいメッシュを追加する
          play();
          // 安全な配列コピーでイテレーション
          const animationGroups = [...scene.animationGroups];
          animationGroups.forEach((group) => {
            group.stop();
            group.dispose();
          });
          scene.animationGroups.length = 0;
          const hitGroundMesh = scene.pick(offset.x, offset.y, (mesh) =>
            mesh.name.includes("ground"),
          );
          const outer = previewedMesh.parent as TransformNode;
          if (
            hitGroundMesh.hit &&
            hitGroundMesh.pickedMesh.position.x === outer.position.x &&
            hitGroundMesh.pickedMesh.position.z === outer.position.z &&
            canPlaceOnGround(hitGroundMesh.pickedMesh, selectedModel.type)
          ) {
            const meshId = nanoid();
            const boundingInfo = previewedMesh.getBoundingInfo();
            const name = `asset_model_layer_${
              hitGroundMesh.pickedMesh.position.x
            }_${hitGroundMesh.pickedMesh.position.z}_${Math.round(
              boundingInfo.boundingBox.maximumWorld.y,
            )}`;
            outer.getChildMeshes().forEach((subMesh) => {
              if (subMesh.material) {
                subMesh.material.alpha = 1;
              }
              if (subMesh.name !== "outer") {
                subMesh.id = meshId;
                subMesh.name = name;
              }
            });
            previewedMesh.metadata["position"] = {
              x: hitGroundMesh.pickedMesh.position.x,
              y: hitGroundMesh.pickedMesh.position.y,
              z: hitGroundMesh.pickedMesh.position.z,
            };
            previewedMesh.metadata["bottomLayer"] = hitGroundMesh.pickedMesh?.name.split("_").pop();
            createGround(
              scene,
              boundingInfo.boundingBox,
              selectedModel.type,
              hitGroundMesh.pickedMesh.position,
              hitGroundMesh.pickedMesh?.name.split("_").pop(),
            );
            handleAction({
              type: ActionCommandType.ADD_MODE,
              model: {
                id: meshId,
                type: selectedModel.type,
                name,
                filename: selectedModel.filename,
                rotation: selectedModel.rotation,
                offset: selectedModel.offset,
                scaling: selectedModel.scaling,
                metadata: selectedModel.metadata,
                position: {
                  x: hitGroundMesh.pickedMesh.position.x,
                  y: hitGroundMesh.pickedMesh.position.y,
                  z: hitGroundMesh.pickedMesh.position.z,
                },
              },
            });
            dispatch(actions.selectSlot(null));
            dispatch(actions.selectedAssetMesh(null));
            dispatch(
              actions.updateActionMenu({
                key: "tap",
                value: true,
                exclusive: true,
              }),
            );
            setPreviewedMesh(null);
          } else {
            (previewedMesh.parent as TransformNode).dispose();
            setPreviewedMesh(null);
          }
        }
        const camera = scene.getCameraByName("camera") as ArcRotateCamera;
        camera?.attachControl(scene.getEngine().getRenderingCanvas(), true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  useEffect(() => {
    if (clientOffset && selectedModel) {
      setOffset({ x: clientOffset.x, y: clientOffset.y });
      const hitGroundMesh = scene.pick(clientOffset.x, clientOffset.y, (mesh) =>
        mesh.name.includes("ground"),
      );
      if (hitGroundMesh.pickedPoint) {
        if (previewedMesh) {
          if (canPlaceOnGround(hitGroundMesh.pickedMesh, selectedModel.type)) {
            (previewedMesh.parent as TransformNode).position =
              hitGroundMesh.pickedMesh.position.clone();
          }
        } else if (!creating) {
          const hitAssetMesh = scene.pick(clientOffset.x, clientOffset.y, (mesh) =>
            mesh.name.includes("asset"),
          );
          if (
            hitAssetMesh.hit &&
            hitGroundMesh.pickedMesh?.name.split("_").pop() ===
              hitAssetMesh.pickedMesh?.name.split("_").pop() &&
            canPlaceOnGround(hitGroundMesh.pickedMesh, selectedModel.type)
          ) {
            setCreating(true);
            scene.getMaterialByName("ground_layer").alpha = 0.3;
            if (
              selectedModel.type === AssetModelType.RIVER ||
              selectedModel.type === AssetModelType.GROUND
            ) {
              scene.getMaterialByName("ground").alpha = 0.3;
            }
            createPreviewMesh(scene, selectedModel.filename, selectedModel.offset)
              .then((mesh) => {
                mesh.getChildMeshes().forEach((subMesh) => {
                  subMesh.material.alpha = 0.8;
                  if (subMesh.name.indexOf("Cube") !== -1) {
                    subMesh.isVisible = false;
                  }
                });
                const outer = createOuterNode(scene, hitGroundMesh.pickedMesh.position);
                if (selectedModel.type === AssetModelType.CHARACTER) {
                  outer.scaling = new Vector3(
                    selectedModel.scaling.x,
                    selectedModel.scaling.y,
                    selectedModel.scaling.z,
                  );
                  scene.animationGroups.forEach((group) => {
                    group.stop();
                  });
                }
                mesh.metadata = {
                  type: selectedModel.type,
                  rotation: selectedModel.rotation,
                };
                mesh.parent = outer;
                setPreviewedMesh(mesh);
                setCreating(false);
              })
              .catch((error) => {
                console.error(error);
                setCreating(false);
              });
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, clientOffset]);
};
